import {
  Box,
  createListCollection,
  Field,
  Flex,
  Heading,
  Portal,
  Select,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { apiFetch } from 'helpers/api';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuUpload, LuX } from 'react-icons/lu';

import type { IdentificationType, IdRequirement } from '@features/onboarding/kycTypes';
import {
  areRequirementsMet,
  emptyIdentityDocsFormState,
  ID_TYPE_LABELS,
  IdentityDocsFormState,
} from '@features/onboarding/kycTypes';

const isFormValid = (state: IdentityDocsFormState, requirements: IdRequirement[]): boolean => {
  if (Object.values(state.errors).some((v) => v != null)) {
    return false;
  }
  return areRequirementsMet(requirements, state.documentUploads);
};

interface Props {
  country: string;
  onFormStateChange: (state: IdentityDocsFormState, isValid: boolean) => void;
}

const IdentityDocumentsStep = ({ country, onFormStateChange }: Props) => {
  const [formState, setFormState] = useState<IdentityDocsFormState>(emptyIdentityDocsFormState());
  const [idRequirements, setIdRequirements] = useState<IdRequirement[]>([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [anyOfSelections, setAnyOfSelections] = useState<Record<number, IdentificationType>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const updateState = useCallback(
    (nextState: IdentityDocsFormState) => {
      setFormState(nextState);
      onFormStateChange(nextState, isFormValid(nextState, idRequirements));
    },
    [onFormStateChange, idRequirements],
  );

  useEffect(() => {
    if (country === '') {
      setIdRequirements([]);
      return;
    }
    let cancelled = false;
    const fetchRequirements = async () => {
      setLoadingRequirements(true);
      try {
        const reqs = await apiFetch<IdRequirement[]>(
          `/kyc/id-requirements?entityType=USER&country=${country}`,
        );
        if (!cancelled) {
          setIdRequirements(reqs);
        }
      } catch {
        if (!cancelled) {
          setIdRequirements([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRequirements(false);
        }
      }
    };
    fetchRequirements();
    return () => {
      cancelled = true;
    };
  }, [country]);

  const onAnyOfSelect = useCallback(
    (groupIndex: number, details: { value: string[] }) => {
      const docType = details.value[0] as IdentificationType | undefined;
      if (docType == null) {
        return;
      }
      const prev = anyOfSelections[groupIndex];
      const nextUploads = { ...formState.documentUploads };
      if (prev != null && prev !== docType) {
        delete nextUploads[prev];
      }
      setAnyOfSelections((s) => ({ ...s, [groupIndex]: docType }));
      updateState({ ...formState, documentUploads: nextUploads });
    },
    [formState, anyOfSelections, updateState],
  );

  const onFileUpload = useCallback(
    (docType: IdentificationType, e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file == null) {
        return;
      }
      updateState({
        ...formState,
        documentUploads: { ...formState.documentUploads, [docType]: file },
      });
    },
    [formState, updateState],
  );

  const onRemoveFile = useCallback(
    (docType: IdentificationType) => {
      const nextUploads = { ...formState.documentUploads };
      delete nextUploads[docType];
      updateState({ ...formState, documentUploads: nextUploads });
      const ref = fileInputRefs.current[docType];
      if (ref != null) {
        ref.value = '';
      }
    },
    [formState, updateState],
  );

  const setFileInputRef = useCallback((docType: string, el: HTMLInputElement | null) => {
    fileInputRefs.current[docType] = el;
  }, []);

  const hasRequirements = idRequirements.some((r) => (r.idTypes?.length ?? 0) > 0);

  return (
    <Flex direction="column" gap={6} maxW="lg">
      <Box>
        <Heading size="md">Identity Documents</Heading>
        <Text color="text.secondary" fontSize="sm" fontWeight="medium" mt={1}>
          Upload the required identity documents for your country.
        </Text>
      </Box>

      {loadingRequirements && (
        <Flex align="center" gap={2}>
          <Spinner size="xs" />
          <Text fontSize="xs" color="text.secondary" fontWeight="medium">
            Loading document requirements...
          </Text>
        </Flex>
      )}

      {!loadingRequirements && hasRequirements && (
        <Flex direction="column" gap={4}>
          {idRequirements.map((req, groupIndex) => {
            const types = req.idTypes ?? [];
            if (types.length === 0) {
              return null;
            }

            if (req.condition === 'ANY_OF') {
              const collection = createListCollection({
                items: types.map((t) => ({ value: t, label: ID_TYPE_LABELS[t] ?? t })),
              });
              const selectedType = anyOfSelections[groupIndex];
              const uploadedFile = selectedType
                ? formState.documentUploads[selectedType]
                : undefined;

              return (
                <Flex key={groupIndex} direction="column" gap={3}>
                  <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                    Upload any one of the following:
                  </Text>

                  <Field.Root>
                    <Field.Label fontSize="sm">Document Type</Field.Label>
                    <Select.Root
                      collection={collection}
                      onValueChange={(d) => onAnyOfSelect(groupIndex, d)}
                      size="md"
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger borderColor="border.light">
                          <Select.ValueText placeholder="Choose which document to upload" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content bg="surface.background">
                            {collection.items.map((item) => (
                              <Select.Item item={item} key={item.value}>
                                {item.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                  </Field.Root>

                  {selectedType != null && (
                    <DocUploadField
                      docType={selectedType}
                      file={uploadedFile}
                      onFileChange={(e) => onFileUpload(selectedType, e)}
                      onRemove={() => onRemoveFile(selectedType)}
                      setRef={(el) => setFileInputRef(selectedType, el)}
                    />
                  )}
                </Flex>
              );
            }

            // ALL_OF: one upload per type
            return (
              <Flex key={groupIndex} direction="column" gap={3}>
                <Text fontSize="xs" color="text.secondary" fontWeight="medium">
                  All of the following documents are required:
                </Text>
                {types.map((docType) => (
                  <DocUploadField
                    key={docType}
                    label={ID_TYPE_LABELS[docType] ?? docType}
                    docType={docType}
                    file={formState.documentUploads[docType]}
                    onFileChange={(e) => onFileUpload(docType, e)}
                    onRemove={() => onRemoveFile(docType)}
                    setRef={(el) => setFileInputRef(docType, el)}
                  />
                ))}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

/** Reusable upload field for a single document type. */
interface DocUploadFieldProps {
  label?: string;
  docType: IdentificationType;
  file: File | undefined;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  setRef: (el: HTMLInputElement | null) => void;
}

const DocUploadField = ({
  label,
  docType,
  file,
  onFileChange,
  onRemove,
  setRef,
}: DocUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleRefSet = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      setRef(el);
    },
    [setRef],
  );

  return (
    <Field.Root w="full">
      {label != null && <Field.Label fontSize="sm">{label}</Field.Label>}
      <input
        ref={handleRefSet}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.docx"
        style={{ display: 'none' }}
        onChange={onFileChange}
      />
      {file != null ? (
        <Flex
          w="full"
          align="center"
          justify="space-between"
          p={3}
          borderWidth={1}
          borderStyle="solid"
          borderColor="border.light"
          borderRadius="md"
          bg="surface.muted"
        >
          <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {file.name}
          </Text>
          <Box
            as="button"
            color="text.secondary"
            onClick={onRemove}
            _hover={{ color: 'text.primary' }}
            ml={2}
            flexShrink={0}
          >
            <LuX size={14} />
          </Box>
        </Flex>
      ) : (
        <Flex
          w="full"
          direction="column"
          align="center"
          justify="center"
          gap={2}
          p={5}
          borderWidth={2}
          borderStyle="dashed"
          borderColor="border.light"
          borderRadius="lg"
          cursor="pointer"
          onClick={() => inputRef.current?.click()}
          _hover={{ borderColor: 'border.dark', bg: 'surface.hover' }}
          transition="all 0.2s"
        >
          <Box color="text.secondary">
            <LuUpload size={18} />
          </Box>
          <Text fontSize="sm" fontWeight="medium">
            Click to upload {label ?? ID_TYPE_LABELS[docType] ?? 'document'}
          </Text>
          <Text fontSize="xs" color="text.secondary" fontWeight="medium">
            PDF, PNG, JPG, or DOCX — up to 50 MB
          </Text>
        </Flex>
      )}
    </Field.Root>
  );
};

export default observer(IdentityDocumentsStep);
